import { Core } from 'core/Core';
import { constrain } from 'utils/math';
import { DragBreakpoints } from 'interaction/DragBreakpoints';
import { Module } from 'core/Module';
import { Events } from 'core/events/Events';

import { CustomCursorEvent } from 'app/events/CustomCursorEvent';
import { InteractiveArea } from 'app/chrome/scrollbar/InteractiveArea';

import { VerticalDragController } from 'app/chrome/verticaldrag/VerticalDragController';
import { ScrollbarController } from 'app/chrome/scrollbar/ScrollbarController';
import { MouseController } from 'app/chrome/mouse/MouseController';
import { Paging } from './Paging';
import { AxisType } from 'app/enum/Geometry';
import DelayHelper from 'utils/timers';
import { Simple } from 'utils/analytics';
import { device } from 'utils/device';

/** To manage side dragging elements  */
export class DragPanel extends Module {
	public config = {
		dragToMoveRatio: 1,
		dragEasePower: 2,
		releaseEasePower: 14,
		snap: false,
		startProgress: 0,
		cursorTheme: 'light',
		velocityFriction: 1,
		totalBreakPoints: 8,
		snapOnlyOnTouch: false,
		snapBelowWidth: 0,
		snapItems: 1,
		pagingId: null
	};

	private _$draw: HTMLElement; //The inner element that slides left / right
	private _$drawItems: NodeListOf<HTMLElement>;
	private _width: number; //The cached clientWidth ofthe draw
	private _x: number = 0; //X position of the drag in px
	private _targetX: number = 0; //X target
	private _maxScroll: number = 0; //Max scroll of the panel (will be a minius number)
	private _dragController: DragBreakpoints; //The controller for the dragging / inernia, borrowed from the home/work timelines
	private _interactiveArea: InteractiveArea; //Tracks if the area should interactive based on drag/scroll state
	private _mouseController: MouseController;
	private _easePower: number;
	private _activeEasePower: number;
	private _dragging: boolean; //True while a pointer drag is in progress
	private _scrollBlock: boolean; //Blocks vertical page scroll while dragging horizontally
	private _sizeSet: boolean = false;

	private _hasScrollBar: boolean = false;
	private _$scrollbar: HTMLElement;
	private _$scrollbarBar: HTMLElement;
	private _scrollbarScale: number;
	private _scrollbarX: number = 100;
	private _breakpointIndex: number = 0;

	private _$next: HTMLElement;
	private _$previous: HTMLElement;

	private _progress: number = 0;
	private _hasArrowGuide: boolean = false;
	private _paging: Paging;

	public create() {
		console.log('Drag Panel');
		this._mouseController = Core.app.controller.get('Mouse') as MouseController;
		this._createInteractiveArea();
		this._easePower = this._activeEasePower = this.config.releaseEasePower;
		this._$draw = this.$container.querySelector('.dragpanel__draw');
		this._$drawItems = this._$draw.querySelectorAll('.dragpanel__item');


		new ResizeObserver(this._onResize.bind(this)).observe(this._$draw);

		this.$container.addEventListener('mouseenter', this._onMouseOver.bind(this));
		this.$container.addEventListener('mouseleave', this._onMouseOut.bind(this));
		this._createDragControl();

		this._$scrollbar = this.$container.querySelector('.drag-panel__scrollbar');
		if (this._$scrollbar) {
			this._createScrollBar();
		}

		this._onResize();

		if (this._$scrollbar) {
			this._updateScrollBar(0);
		}


		this._$next = this.$container.querySelector('.drag-panel-navarrow.-next');
		if (this._$next) {
			this._$previous = this.$container.querySelector('.drag-panel-navarrow.-previous');
			Events.on('click', this._$next, this._nextPress.bind(this));
			Events.on('click', this._$previous, this._previousPress.bind(this));
		}

		if (this.config.pagingId) {
			DelayHelper.setTimeout(() => {
				this._paging = Core.module.get(this.config.pagingId) as Paging;
				this._paging.setCallbacks((index) => {
					this._goto(index);
				});
			}, 500);
		}
	}

	public destroy(): void {
		console.log('Destroy Drag Panel');
		this._dragController.destroy();
		this._dragController = null;
		this._$draw.removeEventListener('resize', this._onResize.bind(this));
		this.$container.removeEventListener('mouseenter', this._onMouseOver.bind(this));
		this.$container.removeEventListener('mouseleave', this._onMouseOut.bind(this));
		this._$draw = null;
		this._$drawItems = null;
		super.destroy();
	}

	public resize(): void {
		this._onResize();
	}

	public tick(): void {
		if (!this._sizeSet && this.$container.clientWidth) {
			this._onResize();
			this._sizeSet = true;
		}

		if (this._sizeSet) {
			this._setDrawPosition();
		}
	}

	//Private
	//--------------------------------------------------------------------------------
	private _createInteractiveArea(): void {
		let scrollbarController = Core.app.controller.get('Scrollbar') as ScrollbarController;
		let vertcialDragController = Core.app.controller.get('VerticalDrag') as VerticalDragController;
		this._interactiveArea = new InteractiveArea(this.$container, scrollbarController, vertcialDragController);

		this._interactiveArea.onThrottleStateResloved = (verticalDragBlocked: boolean) => {
			if (verticalDragBlocked) {
				this._mouseController.resetToHorizontal();
			} else {
				this._mouseController.startVerticalScroll();
			}
		};
	}

	private _onResize(): void {
		this._width = this._$draw.scrollWidth;
		this._maxScroll = -1 * (this._width - this.$container.clientWidth);

		let spacing = parseFloat(window.getComputedStyle(this._$drawItems[0], null).getPropertyValue('margin-right'));

		let snapItems = this.config.snapItems || 1;
		this._dragController.setVirtualWidth(
			this._width,
			this.$container.clientWidth,
			this._$drawItems[0].clientWidth,
			spacing,
			this._$drawItems.length,
			snapItems
		);
		constrain(this._targetX, this._maxScroll, 0);

		if (this.config.snapBelowWidth) {
			this._dragController.snap = device.innerWidth < this.config.snapBelowWidth;
		}

		if (this._hasScrollBar) {
			this._resizeScrollBar();
		}
	}

	private _onMouseOver(e: MouseEvent): void {
		Events.dispatch(
			new CustomCursorEvent({ type: 'hitarea', active: true, id: this.id, theme: this.config.cursorTheme })
		);

		let isDarkTheme = this.config.cursorTheme == 'dark';
		this._interactiveArea.mouseOver();
		this._mouseController.setDragCursorActive(true);
		this._mouseController.setHiddenState(false);
		this._mouseController.setDarkTheme(isDarkTheme);

		if (this._progress == 0) {
			this._hasArrowGuide = true;
			this._mouseController.addArrowGuide('right');
		}
	}

	private _onMouseOut(e: MouseEvent): void {
		Events.dispatch(
			new CustomCursorEvent({ type: 'hitarea', active: false, id: this.id, theme: this.config.cursorTheme })
		);
		this._interactiveArea.mouseOut();
		this._mouseController.setDragCursorActive(false);
		this._mouseController.setHiddenState(true);
		this._mouseController.removeArrowGuides();
	}

	private _createDragControl(): void {
		this.$container.addEventListener(
			'touchmove',
			(e) => {
				if (this._scrollBlock) {
					console.log('CANCL DRAGGING');
					e.preventDefault();
				}
			},
			{ passive: false }
		);

		this._dragController = new DragBreakpoints(this.config.totalBreakPoints, false, 0, false, this.$container);
		this._dragController.eventTag = 'DragPanel';
		this._dragController.setDragToMoveRatio(1);
		this._dragController.easePower = 1;
		this._dragController.releaseEasePower = 1;
		this._dragController.simulateSteps = 30;
		this._dragController.snapOnlyOnTouch = this.config.snapOnlyOnTouch;

		this._dragController.setStartThreshold(0);

		this._dragController.forceSnap = false;
		this._dragController.snap = this.config.snap;


		this._dragController.setProgress(this.config.startProgress);
		this._dragController.setActive(true);

		if (this.config.snap) {
			this._dragController.onSnap = (index) => {
				this._breakpointIndex = index;
				this._setArrowState(this._breakpointIndex);
				this._setPagingState(index);

				let ratio = 0;
				if (this._paging && this._paging.totalItems) {
					ratio = (index + 1) / this._paging.totalItems;
				}

				Simple.event('work_slider_snap', {
					type: this.id,
					breakpoint_index: index,
					progress: Math.round(ratio * 100) / 100
				});
			};
		}

		//Ticker:
		this._dragController.onTick = (progressRatio: number) => {
			this._progress = progressRatio;

			if (this._dragging && Math.abs(this._dragController.lastMoveX) > 0.01) {
				this._scrollBlock = true;
			}

			if (this._hasArrowGuide && this._progress > 0) {
				this._mouseController.removeArrowGuides();
				this._hasArrowGuide = false;
			}


			if (this._mouseController.moveAxis == AxisType.HORIZONTAL) {
				this._targetX = progressRatio * this._maxScroll;
			}

			if (this._targetX != 0) {
			}

			if (this._hasScrollBar) {
				this._updateScrollBar(progressRatio);
			}
		};

		//When the user clicks then stop the drag panel dead:
		this._dragController.onPreDrag = () => {
			this._targetX = this._x;
			this._dragController.setProgress(this._x / this._maxScroll);
		};

		this._dragController.onStartDrag = () => {
			this._easePower = this._activeEasePower;
			this._mouseController.setBothActive(true);
			//Real time drag so ease = 1;
			this._activeEasePower = this.config.dragEasePower;
			this._interactiveArea.interactionStart();
			this._dragging = true;

			this._mouseController.setDragDown(true);
		};

		this._dragController.onStopDrag = () => {
			this._dragging = false;
			this._scrollBlock = false;
			this.DOM.removeClass(this.$container, '-disablescroll');

			Events.dispatch(new CustomCursorEvent({ type: 'drag', active: false, id: this.id }));
			this._easePower = this.config.releaseEasePower;
			this._interactiveArea.interactionEnd();
			this._mouseController.setDragDown(false);
			//Fling effect:


			if (this._dragController.active && this._mouseController.moveAxis == AxisType.HORIZONTAL) {

				let xVel = this._mouseController.getXVelocityAvg();
				let xAdd = 0;

				if (Math.abs(xVel) > 6) {
					xAdd = this._mouseController.getXVelocityAvg() * 10;
					this._dragController.increaseProgressByPixels(xAdd);
				}

				if (this.config.snap) {
					Simple.event('work_slider_progress', {
						progress: Math.round(this._dragController.targetProgress * 100) / 100,
						type: this.id
					});
				}
			}

			this._dragController.setActive(true);
		};
	}

	private _setDrawPosition(): void {
		this._x = this._x + (this._targetX - this._x) / this._easePower;
		this._$draw.style.transform = `translate3d(${this._x}px, 0, 0)`;
	}


	private _createScrollBar(): void {
		this._hasScrollBar = true;
		this._$scrollbarBar = this._$scrollbar.querySelector('.drag-panel__scrollbar__bar');
		this._$scrollbar.querySelector('.drag-panel__scrollbar__track');
	}

	private _resizeScrollBar(): void {
		this._scrollbarScale = device.innerWidth / this._width;
	}

	private _updateScrollBar(progressRatio: number): void {
		this._scrollbarX = ((1.0 - this._scrollbarScale) * progressRatio * 100) / this._scrollbarScale;
		this._$scrollbarBar.style.transform = `scaleX(${this._scrollbarScale}) translateX(${this._scrollbarX}%)`;
	}

	private _nextPress(e: MouseEvent): void {
		e.preventDefault();
		this._goto(this._breakpointIndex + 1);
	}

	private _previousPress(e: MouseEvent): void {
		e.preventDefault();
		this._goto(this._breakpointIndex - 1);
	}

	private _goto(index: number) {
		index = constrain(index, 0, this.config.totalBreakPoints - 1);
		this._dragController.setIndex(index);
		this._setArrowState(index);
		this._setPagingState(index);
	}

	private _setArrowState(index: number) {
		this.DOM.toggleClass(this._$previous, '-deactive', index === 0);
		this.DOM.toggleClass(this._$next, '-deactive', index === this.config.totalBreakPoints - 1);
	}

	private _setPagingState(index: number) {
		if (!this._paging) {
			return;
		}

		this._paging.setActiveIndex(index);
	}
}
